import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import {
  DataRoomRFI,
  DataRoomRFIReport,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import RfiAnalyticsUploaderInstance from './components/RfiAnalyticsUploaderInstance';

import './RfiAnalyticsUploader.styles.scss';

type RfiAnalyticsUploaderProps = {
  className?: string;
  rfi: DataRoomRFI;
  isOpen: boolean;
};

const RfiAnalyticsUploader: React.FC<RfiAnalyticsUploaderProps> = (props) => {
  const { className, rfi, isOpen } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<DataRoomRFIReport[]>([]);

  const [content, contentRef] = useCallbackRef<HTMLDivElement>();
  const isFetched = useRef(false);

  const classes = classNames(
    'rfi-analytics-uploader',
    { loading: isLoading },
    className,
  );

  const { _id } = rfi;

  const { t } = useTranslation();

  const { open, setOpen } = useHeightAnimation({
    element: content,
  });

  const updateReport = useCallback(
    (report: DataRoomRFIReport) =>
      setReports((old) =>
        old.map((el) => (el._id === report._id ? { ...el, ...report } : el)),
      ),
    [],
  );

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen, setOpen]);

  useEffect(() => {
    (async () => {
      if (open && !isFetched.current) {
        setIsLoading(true);
        try {
          const { data } = await dataRoomApi.rfi.getRfiReports(_id);

          setReports(data);
          isFetched.current = true;
        } catch (error) {
          return;
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [_id, open]);

  return (
    <div ref={contentRef} className={classes}>
      <div className="white-panel">
        {isLoading ? (
          <Loader />
        ) : (
          <div>
            <div className="header grid">
              <p>{t('contributor')}</p>
              <p className="uploaded-files">{t('uploadedFiles')}</p>
              <p className="times-opened">{t('timesOpened')}</p>
              <p className="reminders-sent">{t('remindersSent')}</p>
              <p />
            </div>

            {reports.length ? (
              reports.map((report) => (
                <RfiAnalyticsUploaderInstance
                  key={report._id}
                  report={report}
                  rfi={rfi}
                  updateReport={updateReport}
                />
              ))
            ) : (
              <p className="rfi-analytics-uploader__empty">
                {t('noContributors')}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default RfiAnalyticsUploader;
